import styled from "@emotion/styled";
import { SxProps, TextFieldProps } from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Theme } from "klayowebshared";
import React, { Component } from "react";
import { Utils } from "../common/Utils";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import CalendarIcon from "../resources/images/icons-calendar-custom.svg";
import { KLAYO_COLORS } from "../themes";
import { getDateFormatPattern } from "../utilities/formatDate";
import { Alert } from "./Alert";

interface DatePickerProps extends WithLocaleProps {
  allowEmpty?: boolean;
  allowFuture?: boolean;
  allowPast?: boolean;
  showPastWarning?: boolean;
  showPastWarningMessage?: string;
  format?: string;
  maxDate?: Date;
  minDate?: Date;
  value?: Date | null;
  error?: boolean;
  errorMessage?: string | null;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  helperText?: string | null;
  isCustom?: boolean;
  onChange?: (value?: Date | null, valid?: boolean) => void;
  onBlur?: (value?: Date | null, valid?: boolean) => void;
  label?: string | null;
  sx?: SxProps;
  inputFormat?: string;
  textFieldProps?: TextFieldProps & {
    readOnly?: boolean;
  };
  theme?: typeof Theme;
  loading?: boolean;
  validationText?: string | null;
}

interface DatePickerState {
  error?: boolean;
  errorMessage?: string | null;
  value?: Date | null;
  isPast?: boolean;
  format?: string;
}

class DatePickerBase extends Component<DatePickerProps, DatePickerState> {
  static defaultProps = {
    allowEmpty: true,
    allowFuture: true,
    allowPast: true,
    showPastWarning: false,
    // inputFormat: 'dd/MM/yyyy',
    // format: 'dd/MM/yyyy',
    readOnly: false,
    isCustom: true
  };

  ref: React.RefObject<HTMLDivElement | null>;

  constructor(props: DatePickerProps) {
    super(props);

    this.ref = React.createRef();

    const valid = this.isValid(props.value);

    this.state = {
      error: this.props.disabled !== true ? !valid : (props.error ?? false),
      errorMessage: props.errorMessage ?? null,
      value: props.value,
      isPast: valid && this.isDatePast(props.value),
      format: "MM/dd/yyyy"
    };
  }

  componentDidMount() {
    const userLanguage = navigator.language || "en";
    const findFormat = getDateFormatPattern(userLanguage);
    this.setState({ format: findFormat });
  }

  componentDidUpdate(prevProps: DatePickerProps, prevState: DatePickerState) {
    if (prevProps.value !== this.props.value)
      this.setState({
        value: this.props.value,
        error: this.props.disabled !== true ? !this.isValid(this.props.value) : false
      });
    if (prevProps.error !== this.props.error) this.setState({ error: this.props.error });
    if (prevProps.errorMessage !== this.props.errorMessage)
      this.setState({ errorMessage: this.props.errorMessage });
    // allowEmpty change reset error
    if (prevProps.allowEmpty !== this.props.allowEmpty) this.onBlur();
  }

  onChange(value: Date | null) {
    const { onChange } = this.props;

    const valid = this.isValid(value);
    this.setState({ value, error: !valid, isPast: valid && this.isDatePast(value) });

    if (onChange) onChange(value, valid);
  }

  isDatePast(value?: Date | null) {
    if (!value) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return value < today;
  }

  isValid(value?: Date | null) {
    const { allowFuture, allowPast, allowEmpty } = this.props;
    if (!value) {
      return allowEmpty;
    }

    if (!value || typeof value === "string" || !(value instanceof Date) || isNaN(value.getTime())) {
      return false;
    }

    if (!Utils.isExpiryDateValid(value)) return false;

    const today = new Date();
    if (!allowFuture && value > today) return false;
    if (!allowPast && value < today) return false;

    value.setHours(0);
    value.setMinutes(0);

    return true;
  }

  onBlur() {
    const { value } = this.state;
    const isValid = this.isValid(value);

    this.setState({ error: !isValid });

    if (this.props.onBlur) {
      this.props.onBlur(value, isValid);
    }
  }

  renderDatePicker(
    props: MuiDatePickerProps<Date> &
      React.RefAttributes<HTMLDivElement> & {
        inputFormat?: string;
        onBlur?: (value?: Date | null, valid?: boolean) => void;
      }
  ) {
    return <MuiDatePicker {...props} format={this.state.format} />;
  }

  render() {
    const {
      label,
      disabled,
      sx,
      inputFormat,
      readOnly,
      autoFocus,
      helperText,
      showPastWarning,
      showPastWarningMessage,
      format,
      maxDate,
      minDate,
      textFieldProps,
      isCustom,
      translate
    } = this.props;
    const { value, error, isPast } = this.state;
    const errorMessage =
      this.state.errorMessage ?? translate?.("common.invalidDate") ?? "Invalid date";
    const pastWarningMessage =
      showPastWarningMessage ??
      translate?.("common.selectCurrentOrFutureDate") ??
      "Please select a current or future date";

    return (
      <DatePickerStyled
        className={`klayo_date-picker ${disabled ? "picker-disabled" : ""}`}
        isCustom={isCustom}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {label && (
            <label
              className={`date-picker-label ${error ? "date-picker-label-error" : ""} ${disabled ? "date-picker-label-disabled" : ""}`}
            >
              {label}
            </label>
          )}

          {this.renderDatePicker({
            ref: this.ref,
            inputFormat: inputFormat,
            onChange: this.onChange.bind(this),
            value: value,
            disabled: disabled,
            readOnly: readOnly,
            format: format,
            slots: {
              openPickerIcon: CalendarIcon
            },
            maxDate: maxDate,
            minDate: minDate,
            onBlur: this.onBlur.bind(this),
            autoFocus: autoFocus,
            slotProps: {
              textField: {
                error: error,
                helperText: error ? errorMessage : helperText,
                ...textFieldProps
              }
            },
            sx
          })}

          {showPastWarning && isPast && (
            <Alert severity='warning' sx={{ marginTop: "16px" }}>
              {pastWarningMessage}
            </Alert>
          )}
        </LocalizationProvider>
      </DatePickerStyled>
    );
  }
}

const DatePickerStyled = styled.div<{ isCustom?: boolean }>`
  &.klayo_date-picker {
    // max-width: 200px;

    .date-picker-label {
      font-size: 14px;
      font-weight: 500;
      font-style: normal;
      line-height: 20px;
      color: ${KLAYO_COLORS.GreyGR700};
      padding-left: 14px;
      padding-bottom: 5px;
      display: block;
    }

    .MuiInputBase-input {
      padding: 9.5px 14px;
    }

    .MuiOutlinedInput-root {
      border-radius: 20px;
    }

    .Mui-focused {
      .MuiOutlinedInput-notchedOutline {
        border-color: ${KLAYO_COLORS.NeonBlueNB400};
      }
      .MuiIconButton-root {
        color: ${KLAYO_COLORS.NeonBlueNB500};
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ isCustom }) => (isCustom ? KLAYO_COLORS.NeonBlueNB400 : "")};
    }

    .MuiIconButton-root {
      color: ${({ isCustom }) => (isCustom ? KLAYO_COLORS.NeonBlueNB500 : "")};
    }
  }
  &.picker-disabled {
    .MuiTextField-root {
      .MuiInputBase-root {
        background-color: ${KLAYO_COLORS.GreyGR100};
      }
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${KLAYO_COLORS.GreyGR300};
    }
    .MuiIconButton-root {
      color: ${KLAYO_COLORS.GreyGR400};
    }
  }
`;

export const DatePicker = withLocale(DatePickerBase);
